/**
 * Section-kind registry: which component renders which deterministic section.
 *
 * The backend's `kind` is a registry key string, and this is the client half
 * of the same registry. Two properties matter:
 *
 * A kind this build does not recognise resolves to `undefined`, and the
 * caller renders the section's body read-only rather than treating it as an
 * ordinary editable section. That is what makes a backend-first rollout safe:
 * the server can ship a kind, notes carrying it render their text, and the
 * component arrives later. Note that "unknown" is different from "has no
 * kind" — the placeholder gate and the editable/read-only decision both key
 * on *having* a kind, never on this lookup.
 *
 * Adding a kind is an entry in this map plus its component.
 */
import type React from 'react';

import { ChartSectionBlock } from './ChartSectionBlock';

export interface SectionKindComponentProps {
  /** The registry key this section declared. */
  kind: string;
  /** Section name, rendered in the shared note section heading. */
  name: string;
  /**
   * Whether the note is in a view mode that allows interaction. A
   * kind-bearing section renders its body in every view mode; this only
   * chooses between the interactive surface and the read-only one.
   */
  editable: boolean;
  /** Patient whose chart or record the section reads. */
  patientId?: string;
  /** The section's resolved body, for the read-only rendering. */
  markdown: string;
}

export const SECTION_KIND_COMPONENTS: Record<
  string,
  React.ComponentType<SectionKindComponentProps>
> = {
  active_diagnoses: ChartSectionBlock,
  medications: ChartSectionBlock,
  allergies: ChartSectionBlock,
};

export const getSectionKindComponent = (
  kind: string | undefined,
): React.ComponentType<SectionKindComponentProps> | undefined =>
  kind ? SECTION_KIND_COMPONENTS[kind] : undefined;
